import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import { useQueryClient } from 'react-query';
import { REPORTS_KEY } from '@/utils/hooks/reports';
import { fetchReport } from '@/utils/api/reports.api';
import useSaveReportMutation from './useSaveReportMutation';

const FLUSH_DELAY_MS = 700;

/**
 * Coordinates report autosave: debounced, single-flight with exactly one
 * trailing save, optimistic + version-tracked, flush-on-blur, and gated
 * refetch-on-focus. `save(config)` is the single entry point for every writer
 * (widgets, rename, synthesis) — callers pass only the config; the saver owns
 * the report id and the expected version.
 *
 * `version` is the last-known server version from the report query. The saver
 * also advances its own versionRef synchronously after each save so a trailing
 * PUT carries the new expected_version and never self-conflicts.
 */
const useReportSaver = (reportId, version) => {
    const qc = useQueryClient();

    // Refs are the synchronous source of truth read inside async callbacks; only
    // `conflict` needs to be state (it drives the dialog).
    const pendingConfigRef = useRef(null); // latest full config, null when clean
    const versionRef = useRef(null); // last-known server version
    const isSavingRef = useRef(false); // single-flight gate
    const hasTrailingRef = useRef(false); // exactly one trailing save (a flag)
    const conflictRef = useRef(null); // blocks saving while a conflict is open
    const mountedRef = useRef(true);
    const hadVersionRef = useRef(false);

    const [conflict, setConflict] = useState(null);

    const openConflict = useCallback((body) => {
        // body: { version, config } — the current server state.
        conflictRef.current = body ?? { version: null, config: null };
        if (mountedRef.current) setConflict(conflictRef.current);
    }, []);

    const mutation = useSaveReportMutation(reportId, {
        onVersionAdvanced: (next) => {
            versionRef.current = next;
        },
        onConflict: openConflict,
    });

    // flushNow lives behind a ref so the once-created debounce always invokes the
    // latest closure; recreating the debounce each render would reset its timer
    // and break coalescing.
    const flushNowRef = useRef(() => {});
    flushNowRef.current = () => {
        if (conflictRef.current) return; // blocked while a conflict is open
        if (isSavingRef.current) {
            hasTrailingRef.current = true; // exactly one trailing save
            return;
        }
        const config = pendingConfigRef.current;
        if (config == null) return; // nothing dirty
        if (versionRef.current == null) return; // report not created/loaded yet

        isSavingRef.current = true;
        mutation.mutate(
            { config, expectedVersion: versionRef.current },
            {
                onSuccess: () => {
                    // Clear pending iff no newer edit superseded what we sent.
                    if (pendingConfigRef.current === config) {
                        pendingConfigRef.current = null;
                    }
                },
                onSettled: () => {
                    isSavingRef.current = false;
                    // Fire exactly one trailing save (latest state + freshly
                    // advanced version), unless a conflict opened.
                    if (hasTrailingRef.current && !conflictRef.current) {
                        hasTrailingRef.current = false;
                        flushNowRef.current();
                    }
                },
            },
        );
    };

    const debouncedFlush = useMemo(
        () => debounce(() => flushNowRef.current(), FLUSH_DELAY_MS),
        [],
    );

    // `save` takes an updater `(prevConfig) => nextConfig` (or a plain config).
    // The updater builds on the current pending config, or the live cache config
    // when clean — so two saves within one debounce window that touch different
    // keys (e.g. synthesis writing `analysis` while a widget edit writes
    // `widgets`) accumulate instead of clobbering each other.
    const save = useCallback(
        (configOrUpdater) => {
            const base =
                pendingConfigRef.current ??
                qc.getQueryData([REPORTS_KEY, reportId])?.config ??
                {};
            pendingConfigRef.current =
                typeof configOrUpdater === 'function'
                    ? configOrUpdater(base)
                    : configOrUpdater;
            debouncedFlush();
        },
        [debouncedFlush, qc, reportId],
    );

    const flush = useCallback(() => {
        debouncedFlush.cancel();
        flushNowRef.current();
    }, [debouncedFlush]);

    // Reset the machine when the report id changes; seed versionRef from the new
    // report's version (referenced, not depended on — depending on `version`
    // would wipe pending edits on every save's version bump).
    useEffect(() => {
        pendingConfigRef.current = null;
        hasTrailingRef.current = false;
        conflictRef.current = null;
        hadVersionRef.current = false;
        setConflict(null);
        versionRef.current = version ?? null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportId]);

    // Keep versionRef synced with the report query (initial load, focus-refetch,
    // reload). If edits were queued before the report had a version (fresh
    // create), flush them once the version first becomes known.
    useEffect(() => {
        if (version != null) versionRef.current = version;
        if (version != null && !hadVersionRef.current) {
            hadVersionRef.current = true;
            if (pendingConfigRef.current != null) flush();
        }
    }, [version, flush]);

    // Flush pending edits on the way out so nothing is lost when tabbing away.
    useEffect(() => {
        const onHide = () => {
            if (document.visibilityState === 'hidden') flush();
        };
        const onBlur = () => flush();
        document.addEventListener('visibilitychange', onHide);
        window.addEventListener('blur', onBlur);
        window.addEventListener('pagehide', onBlur);
        return () => {
            document.removeEventListener('visibilitychange', onHide);
            window.removeEventListener('blur', onBlur);
            window.removeEventListener('pagehide', onBlur);
        };
    }, [flush]);

    // Gated refetch-on-focus: clean -> pull latest; dirty + server advanced ->
    // conflict (never blind-overwrite unsaved edits); dirty + unchanged -> let the
    // queued save proceed.
    useEffect(() => {
        const onFocus = async () => {
            if (conflictRef.current) return;
            const hasPending =
                pendingConfigRef.current != null ||
                isSavingRef.current ||
                hasTrailingRef.current;
            if (!hasPending) {
                qc.invalidateQueries([REPORTS_KEY, reportId]);
                return;
            }
            try {
                const fresh = await fetchReport(reportId);
                if (
                    mountedRef.current &&
                    versionRef.current != null &&
                    fresh.version > versionRef.current
                ) {
                    openConflict({
                        version: fresh.version,
                        config: fresh.config,
                    });
                }
            } catch {
                // offline / 404: keep local edits; the next PUT surfaces the error.
            }
        };
        const onVisible = () => {
            if (document.visibilityState === 'visible') onFocus();
        };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onFocus);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', onFocus);
        };
    }, [reportId, qc, openConflict]);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            debouncedFlush.cancel(); // drop the pending timer; no PUT on unmount
        };
    }, [debouncedFlush]);

    const dismissConflict = useCallback(() => {
        // Re-enable saving; the next conflicting save re-opens the dialog (a nag
        // until the user reloads) rather than silently blocking forever.
        conflictRef.current = null;
        setConflict(null);
    }, []);

    const reloadFromServer = useCallback(async () => {
        pendingConfigRef.current = null;
        hasTrailingRef.current = false;
        conflictRef.current = null;
        setConflict(null);
        await qc.invalidateQueries([REPORTS_KEY, reportId]);
        const fresh = qc.getQueryData([REPORTS_KEY, reportId]);
        if (fresh?.version != null) versionRef.current = fresh.version;
    }, [qc, reportId]);

    return { save, flush, conflict, dismissConflict, reloadFromServer };
};

export default useReportSaver;
